/**
 * ForecastCard — proyección de paquetes del día (forecast_daily_snapshots vía
 * microservice-forecasting, métrica "created"). Muestra la predicción puntual
 * del día, su rango 80% y el avance real recibido vs lo proyectado.
 */
import { useTranslation } from 'react-i18next';
import { TrendingUp } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { Skeleton } from '../../../components/ui/Skeleton';
import { PendingBackendNotice } from '../../../components/ui/PendingBackendNotice';
import { formatNumberCL } from '../../../utils/formatters';

export default function ForecastCard({ forecast, actual, loading }) {
  const { t } = useTranslation();

  if (loading || forecast?.loading) return <Skeleton className="h-28 rounded-xl" />;

  const projected = forecast?.projected;
  const pct = forecast?.available && projected > 0 ? Math.min((actual / projected) * 100, 100) : 0;

  return (
    <Card padding="md">
      <div className="mb-3 flex items-center gap-2">
        <span className="grid h-8 w-8 place-items-center rounded-lg bg-brand-50 text-brand-700">
          <TrendingUp className="h-4 w-4" strokeWidth={2.2} />
        </span>
        <h3 className="text-sm font-semibold text-fg">{t('Proyección del día')}</h3>
      </div>

      {!forecast?.available ? (
        <PendingBackendNotice>
          {t('Aún no hay proyección para hoy.')}
        </PendingBackendNotice>
      ) : (
        <>
          <div className="flex items-end gap-3">
            <span className="text-4xl font-bold tabular-nums leading-none text-fg">
              {formatNumberCL(Math.round(projected))}
            </span>
            <span className="pb-1 text-[11px] text-fg-subtle">{t('paquetes esperados')}</span>
          </div>

          {(forecast.lo != null && forecast.hi != null) && (
            <div className="mt-1 text-[11px] text-fg-muted">
              {t('Rango')} {formatNumberCL(Math.round(forecast.lo))}–{formatNumberCL(Math.round(forecast.hi))}
            </div>
          )}

          <div className="mt-3">
            <div className="mb-1 flex items-center justify-between text-[11px] text-fg-muted">
              <span>{t('Recibidos hoy')}</span>
              <span className="font-semibold tabular-nums text-fg">
                {formatNumberCL(actual || 0)} · {Math.round(pct)}%
              </span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-bg-muted">
              <div
                className="h-full rounded-full bg-brand-500 transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        </>
      )}
    </Card>
  );
}
